import {
	createSlice,
	type EntityState,
	type PayloadAction
} from '@reduxjs/toolkit'
import { createSetter } from 'shared/redux-helpers'

import type {
	Conversation,
	Filter,
	GetProps,
	Message,
	UpdateConversation
} from './types'

import { api } from '../api'
import { adapters } from '../utils'

interface EditMessage {
	id: string
	text: string
}

interface Slice extends GetProps {
	conversations: EntityState<Conversation, string>
	editMessage: EditMessage | null
	focusedInboxEnabled: boolean
	messages: EntityState<Message, string>
	messageToDelete: string
}

const initialState: Slice = {
	conversations: adapters.conversations.getInitialState(),
	editMessage: null,
	filter: 'primary',
	focusedInboxEnabled: false,
	messages: adapters.messages.getInitialState(),
	messageToDelete: '',
	search: ''
}

function resetFilter(state: Slice) {
	setFilter(state, state.focusedInboxEnabled ? 'focused' : 'primary')
}

function setFilter(state: Slice, filter: Filter) {
	state.filter = filter
	setSearch(state, '')
}

function setSearch(state: Slice, search: string) {
	state.search = search
}

const tabFilters = new Set<Filter>(['focused', 'other'])

const slice = createSlice({
	extraReducers: builder =>
		builder.addMatcher(
			api.endpoints.settings.matchFulfilled,
			(state, { payload: { focusedInboxEnabled } }) => {
				state.focusedInboxEnabled = focusedInboxEnabled
				resetFilter(state)
			}
		),

	initialState,
	name: 'messaging',
	reducers: {
		addConversations(state, action: PayloadAction<Conversation[]>) {
			adapters.conversations.upsertMany(state.conversations, action)
		},

		addMessages(state, action: PayloadAction<Message[]>) {
			adapters.messages.upsertMany(state.messages, action)
		},
		resetDeleteMessage(state) {
			state.messageToDelete = ''
		},
		resetEditMessage(state) {
			state.editMessage = null
		},
		resetFilter,
		resetSearch: state => setSearch(state, ''),

		setEditMessage: createSetter<Slice>('editMessage'),
		setFilter: (state, { payload }: PayloadAction<Filter>) =>
			setFilter(state, payload),
		setMessageToDelete: createSetter<Slice>('messageToDelete'),

		setSearch: (state, { payload }: PayloadAction<string>) =>
			setSearch(state, payload),
		updateMessage(
			state,
			action: PayloadAction<{ changes: Partial<Message>; id: string }>
		) {
			adapters.messages.updateOne(state.messages, action.payload)
		},

		updateRead(
			state,
			{ payload: { id, mark } }: PayloadAction<UpdateConversation>
		) {
			adapters.conversations.updateOne(state.conversations, {
				changes: { unread_count: Number(!mark) },
				id
			})
		},
		updateStar(
			state,
			{ payload: { id, mark } }: PayloadAction<UpdateConversation>
		) {
			adapters.conversations.updateOne(state.conversations, {
				changes: { is_starred: mark },
				id
			})
		}
	},

	selectors: {
		/**
		 * ! For entity adapter only
		 * */
		conversations: state => state.conversations,
		deleteMessage: state => state.messageToDelete,
		editMessage: state => state.editMessage,
		filter: state => state.filter,
		/**
		 * ! For entity adapter only
		 * */
		messages: state => state.messages,
		search: state => state.search,
		searchMode: state => Boolean(state.search),
		withTabs: ({ filter, search }) => Boolean(!search && tabFilters.has(filter))
	}
})

const actions = slice.actions

export { actions, slice }
